// v10/src/utils/flightLogSpreadsheet.js
// Import de planillas históricas desde archivo Excel/CSV.
//
// Este util es la entrada "por archivo" al mismo flujo que ya usa el import
// por foto (Claude Vision). El parseo corre 100% en el frontend con SheetJS
// (xlsx, ya en package.json). El output son planillas con la MISMA forma que
// produce visionRowToEditable en AdminFlightLogImport.js, para que caigan
// en la tabla de revisión sin tocar nada.
//
// Contrato:
//   - Cada archivo puede traer vuelos de varias aeronaves y meses mezclados.
//     El parser agrupa por (matrícula + YYYY-MM de la fecha) y arma UNA
//     planilla por grupo.
//   - Enums estrictos: purpose y payment_method. Si el valor del archivo no
//     coincide con el enum, la celda queda vacía + _low=true (ámbar en UI).
//   - Fecha defensiva: acepta string DD/MM/AAAA, YYYY-MM-DD, Date object
//     (xlsx con cellDates:true) y número serial de Excel.
//   - Matrícula sin match contra aircraftList ⇒ aircraftId='' + registrationText
//     seteado. El auto-match del componente ya reintenta y validatePlanilla
//     marca "Revisar" hasta que el admin la asigne.

import * as XLSX from 'xlsx';

// Enums del backend (flightLogImportController.js). Mantener idénticos —
// el backend rechaza cualquier otro valor con 400.
const PURPOSE_VALUES = ['ENT', 'VP', 'ADAP', 'READAP', 'INST', 'Bautismo', 'Excursion', 'Otros'];
const PAYMENT_VALUES = ['Transferencia', 'Cuenta corriente', 'Pagado', 'Paquete', 'Efectivo'];

// Headers canónicos de la plantilla (es-AR). El orden acá define el orden
// del CSV que descarga el usuario. La tolerancia al orden en la entrada la
// da el HEADER_ALIASES map.
export const TEMPLATE_HEADERS = [
  'matricula',
  'fecha',
  'piloto',
  'finalidad',
  'detalle_finalidad',
  'salida',
  'desde',
  'hasta',
  'llegada',
  'tiempo_vuelo',
  'aterrizajes',
  'aceite',
  'instructor',
  'hobbs_inicio',
  'hobbs_fin',
  'pago',
  'observaciones',
];

// Header normalizado (input del archivo) → clave canónica interna.
// Aliases razonables para tolerar variaciones comunes ("hobbs inicial",
// "forma de pago", "detalle de la finalidad"). Cualquier otro variante,
// el usuario descarga la plantilla y matchea nombres.
const HEADER_ALIASES = {
  matricula: 'matricula',
  aeronave: 'matricula',
  fecha: 'fecha',
  piloto: 'piloto',
  pilot: 'piloto',
  finalidad: 'finalidad',
  objetivo: 'finalidad',
  detallefinalidad: 'detalle_finalidad',
  detalledelafinalidad: 'detalle_finalidad',
  detalle: 'detalle_finalidad',
  salida: 'salida',
  desde: 'desde',
  origen: 'desde',
  hasta: 'hasta',
  destino: 'hasta',
  llegada: 'llegada',
  tiempovuelo: 'tiempo_vuelo',
  tiempodevuelo: 'tiempo_vuelo',
  aterrizajes: 'aterrizajes',
  aterrizaje: 'aterrizajes',
  aceite: 'aceite',
  instructor: 'instructor',
  hobbsinicio: 'hobbs_inicio',
  hobbsinicial: 'hobbs_inicio',
  horometroinicio: 'hobbs_inicio',
  horometroinicial: 'hobbs_inicio',
  hobbsfin: 'hobbs_fin',
  hobbsfinal: 'hobbs_fin',
  horometrofin: 'hobbs_fin',
  horometrofinal: 'hobbs_fin',
  pago: 'pago',
  formadepago: 'pago',
  observaciones: 'observaciones',
  observacion: 'observaciones',
  notas: 'observaciones',
};

// "Matrícula" → "matricula" (lowercase, sin acentos, sin espacios/símbolos).
export const normalizeHeader = (s) => String(s == null ? '' : s)
  .toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[\s_\-./]/g, '');

// Normalización de matrícula igual a la del componente: strip espacios/guiones,
// uppercase. "LV-ABC" == "LVABC" == "lv abc".
const normalizeRegistration = (s) => String(s == null ? '' : s)
  .replace(/[\s\-]/g, '')
  .toUpperCase();

// ── Fecha defensiva ──────────────────────────────────────────────────
// Devuelve { day, month, year } o null si no parsea.
// Casos que soporta:
//   - Date object (xlsx entrega esto cuando la celda es fecha y read({cellDates:true}))
//   - "DD/MM/AAAA" (formato es-AR de la plantilla)
//   - "YYYY-MM-DD" (por si viene ISO)
//   - Número serial de Excel (raro con cellDates, pero puede llegar de CSV)
export const parseSpreadsheetDate = (v) => {
  if (v == null || v === '') return null;

  if (v instanceof Date && !isNaN(v.getTime())) {
    // Usar getters locales — xlsx con cellDates entrega Date en TZ local.
    return { day: v.getDate(), month: v.getMonth() + 1, year: v.getFullYear() };
  }

  const s = String(v).trim();
  if (!s) return null;

  // DD/MM/AAAA
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const year = parseInt(m[3], 10);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) return { day, month, year };
  }

  // YYYY-MM-DD
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const day = parseInt(m[3], 10);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) return { day, month, year };
  }

  // DD-MM-AAAA (variante con guiones)
  m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const year = parseInt(m[3], 10);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) return { day, month, year };
  }

  // Número serial de Excel. Epoch = 1899-12-30 (compensa el bug de 1900 no
  // bisiesto). n=1 ⇒ 1899-12-31. n=45658 ⇒ 2025-01-01.
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = parseFloat(s);
    if (n > 0 && n < 100000) {
      const epoch = Date.UTC(1899, 11, 30);
      const d = new Date(epoch + Math.floor(n) * 86400000);
      if (!isNaN(d.getTime())) {
        return { day: d.getUTCDate(), month: d.getUTCMonth() + 1, year: d.getUTCFullYear() };
      }
    }
  }

  return null;
};

// Enum matcher case-insensitive. Match exacto contra el valor canónico
// (respetando su casing), o null si no matchea.
const matchEnum = (raw, values) => {
  const s = String(raw == null ? '' : raw).trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  return values.find(v => v.toLowerCase() === lower) || null;
};

// Convierte string tolerante a coma decimal en float. Devuelve NaN si no parsea.
const toFloatLoose = (v) => {
  if (v == null || v === '') return NaN;
  if (typeof v === 'number') return v;
  const s = String(v).trim().replace(',', '.').replace(/[^\d.\-]/g, '');
  return s === '' ? NaN : parseFloat(s);
};

const strOr = (v) => (v == null ? '' : String(v).trim());

// "1:30" o "1.5" → string listo para el payload. La conversión decimal la
// hace parseFlightTime en el server y en el component. Acá solo garantizamos
// que sea texto plano (xlsx puede entregar number).
const stringifyFlightTime = (v) => {
  if (v == null || v === '') return '';
  if (typeof v === 'number') {
    // Excel devuelve fracción de día si la celda es tipo tiempo (0.0625 = 1:30h).
    // Heurística: si es < 24 lo tratamos como horas decimales (1.5 ⇒ "1.5").
    // Si es < 1, probablemente sea fracción de día ⇒ convertir a horas.
    if (v < 1) {
      const totalMin = Math.round(v * 24 * 60);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      return `${h}:${String(m).padStart(2, '0')}`;
    }
    return String(v);
  }
  return String(v).trim();
};

// ── Estructura de una fila editable ──────────────────────────────────
// Debe coincidir con la forma que produce visionRowToEditable() en
// AdminFlightLogImport.js. Si esa forma cambia, hay que sincronizar acá.
const buildEditableRow = ({
  day, pilot, purpose, purpose_other, dep_time, from, to, arr_time,
  flight_time, landings, oil, instructor, hobbs_start, hobbs_end,
  payment_method,
}, low) => ({
  id: null,
  day: day == null ? '' : String(day),
  pilot: pilot || '',
  purpose: purpose || '',
  purpose_other: purpose_other || '',
  dep_time: dep_time || '',
  from: from || '',
  to: to || '',
  arr_time: arr_time || '',
  flight_time: flight_time || '',
  landings: landings == null ? '' : String(landings),
  oil: oil || '',
  instructor: instructor || '',
  hobbs_start: hobbs_start == null || hobbs_start === '' ? '' : String(hobbs_start),
  hobbs_end: hobbs_end == null || hobbs_end === '' ? '' : String(hobbs_end),
  payment_method: payment_method || '',
  recorded_by: '',
  instructor_user_id: '',
  notes_extra: '',
  _low: {
    day: !!low.day,
    pilot: !!low.pilot,
    purpose: !!low.purpose,
    dep_time: !!low.dep_time,
    from: !!low.from,
    to: !!low.to,
    arr_time: !!low.arr_time,
    flight_time: !!low.flight_time,
    landings: !!low.landings,
    oil: !!low.oil,
    instructor: !!low.instructor,
    hobbs_start: !!low.hobbs_start,
    hobbs_end: !!low.hobbs_end,
    payment_method: !!low.payment_method,
  },
});

// ── Parser principal ─────────────────────────────────────────────────
// Args:
//   file: File del <input type=file>
//   opts.aircraftList: array de aeronaves de la org, para auto-match.
//   opts.maxPlanillas: límite de planillas que se pueden ADICIONAR al lote
//                     (calculado por el caller como MAX_TOTAL - ya_agregadas).
// Return: { planillas, error?, warning? }
//   - error: string ⇒ no importar nada.
//   - warning: string ⇒ importar lo que hay, mostrar aviso.
export const parseSpreadsheetFile = async (file, opts = {}) => {
  const { aircraftList = [], maxPlanillas = Infinity } = opts;

  let workbook;
  try {
    const buf = await file.arrayBuffer();
    workbook = XLSX.read(buf, { type: 'array', cellDates: true, raw: false });
  } catch (err) {
    return { planillas: [], error: `No se pudo leer el archivo: ${err.message || 'formato no válido'}.` };
  }

  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    return { planillas: [], error: 'El archivo no tiene hojas de cálculo.' };
  }
  const sheet = workbook.Sheets[sheetName];

  // sheet_to_json con defval:null da uniformidad de columnas por fila.
  // header:1 nos devolvería arrays; usamos default (objects por headers de
  // la primera fila) para que el HEADER_ALIASES matchee por nombre.
  const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: null, raw: false });
  if (!rawRows.length) {
    return { planillas: [], error: 'La hoja no tiene filas de datos.' };
  }

  // Mapeo columnaOriginal → claveCanonica. Usamos las keys del primer objeto
  // (que representan los headers efectivos del archivo).
  const originalHeaders = Object.keys(rawRows[0]);
  const colMap = {}; // { claveCanonica: 'header original en el archivo' }
  for (const h of originalHeaders) {
    const norm = normalizeHeader(h);
    const canon = HEADER_ALIASES[norm];
    if (canon && !colMap[canon]) colMap[canon] = h;
  }

  const missing = [];
  if (!colMap.matricula) missing.push('matricula');
  if (!colMap.fecha) missing.push('fecha');
  if (missing.length) {
    return {
      planillas: [],
      error: `Faltan columnas obligatorias en el archivo: ${missing.join(', ')}. Descargá la plantilla vacía para ver los nombres esperados.`,
    };
  }

  // Índice matrícula normalizada → aeronave, para auto-match.
  const aircraftByReg = new Map();
  for (const a of aircraftList) {
    const key = normalizeRegistration(a.registration);
    if (key) aircraftByReg.set(key, a);
  }

  // ── Agrupación por (matrícula normalizada + YYYY-MM) ──────────────
  const groups = new Map(); // key → { registrationText, aircraftId, year, month, rows[] }
  const skipped = []; // filas sin matrícula o sin fecha válida
  let rowIdx = 0;
  for (const row of rawRows) {
    rowIdx++;
    const matriculaRaw = strOr(row[colMap.matricula]);
    const fechaRaw = row[colMap.fecha]; // puede ser Date, string o number
    if (!matriculaRaw) { skipped.push({ line: rowIdx + 1, reason: 'sin matrícula' }); continue; }
    const parsedDate = parseSpreadsheetDate(fechaRaw);
    if (!parsedDate) { skipped.push({ line: rowIdx + 1, reason: 'fecha inválida' }); continue; }

    const regKey = normalizeRegistration(matriculaRaw);
    const yearMonthKey = `${parsedDate.year}-${String(parsedDate.month).padStart(2, '0')}`;
    const groupKey = `${regKey}::${yearMonthKey}`;

    // Datos de fila
    const purposeRaw = strOr(row[colMap.finalidad]);
    const purposeMatched = matchEnum(purposeRaw, PURPOSE_VALUES);
    const paymentRaw = strOr(row[colMap.pago]);
    const paymentMatched = matchEnum(paymentRaw, PAYMENT_VALUES);

    const hobbsStartRaw = row[colMap.hobbs_inicio];
    const hobbsEndRaw = row[colMap.hobbs_fin];
    const hsNum = toFloatLoose(hobbsStartRaw);
    const heNum = toFloatLoose(hobbsEndRaw);
    const hobbsPairInvalid = !isNaN(hsNum) && !isNaN(heNum) && heNum < hsNum;

    const landingsRaw = row[colMap.aterrizajes];
    let landingsVal = '';
    let landingsLow = false;
    if (landingsRaw != null && strOr(landingsRaw) !== '') {
      const n = parseInt(strOr(landingsRaw), 10);
      if (isNaN(n) || n < 0) { landingsLow = true; }
      else landingsVal = String(n);
    }

    const dayVal = parsedDate.day;
    const dayInvalid = !(dayVal >= 1 && dayVal <= 31);

    const editable = buildEditableRow(
      {
        day: dayVal,
        pilot: strOr(row[colMap.piloto]),
        purpose: purposeMatched || '',
        purpose_other: purposeMatched === 'Otros' ? strOr(row[colMap.detalle_finalidad]) : '',
        dep_time: strOr(row[colMap.salida]),
        from: strOr(row[colMap.desde]),
        to: strOr(row[colMap.hasta]),
        arr_time: strOr(row[colMap.llegada]),
        flight_time: stringifyFlightTime(row[colMap.tiempo_vuelo]),
        landings: landingsVal,
        oil: strOr(row[colMap.aceite]),
        instructor: strOr(row[colMap.instructor]),
        hobbs_start: !isNaN(hsNum) ? String(hsNum) : '',
        hobbs_end: !isNaN(heNum) ? String(heNum) : '',
        payment_method: paymentMatched || '',
      },
      {
        day: dayInvalid,
        purpose: purposeRaw !== '' && !purposeMatched,
        payment_method: paymentRaw !== '' && !paymentMatched,
        landings: landingsLow,
        hobbs_start: hobbsPairInvalid,
        hobbs_end: hobbsPairInvalid,
      }
    );

    let group = groups.get(groupKey);
    if (!group) {
      const matchedAircraft = aircraftByReg.get(regKey) || null;
      group = {
        key: groupKey,
        registrationText: matriculaRaw,
        aircraftId: matchedAircraft ? matchedAircraft.id : '',
        year: parsedDate.year,
        month: parsedDate.month,
        rows: [],
      };
      groups.set(groupKey, group);
    }
    group.rows.push(editable);
  }

  if (!groups.size) {
    return {
      planillas: [],
      error: 'No se pudieron procesar filas del archivo (revisá que las columnas matricula y fecha tengan valores).',
    };
  }

  // Orden estable: por año-mes desc y matrícula asc (arriba lo más reciente).
  const planillas = Array.from(groups.values())
    .sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      if (a.month !== b.month) return b.month - a.month;
      return a.registrationText.localeCompare(b.registrationText);
    })
    .map(g => ({
      // id / createdAt los completa el caller (usa su genPlanillaId).
      file: null,
      thumbnail: null,
      aircraftId: g.aircraftId,
      year: String(g.year),
      month: String(g.month),
      registrationText: g.registrationText,
      note: '',
      rows: g.rows,
      status: 'scanned',
      error: null,
      importId: null,
      source: 'spreadsheet',
    }));

  if (planillas.length > maxPlanillas) {
    return {
      planillas: [],
      error: `El archivo contiene ${planillas.length} planillas (aeronave × mes) y el máximo permitido en un lote es ${maxPlanillas}. Subí un archivo con menos meses o aeronaves, o dividilo en tandas.`,
    };
  }

  let warning = null;
  if (skipped.length) {
    const preview = skipped.slice(0, 3).map(s => `fila ${s.line} (${s.reason})`).join(', ');
    const more = skipped.length > 3 ? ` y ${skipped.length - 3} más` : '';
    warning = `Se saltearon ${skipped.length} filas del archivo: ${preview}${more}. El resto se importó normalmente.`;
  }

  return { planillas, warning };
};

// ── Descarga de plantilla vacía ──────────────────────────────────────
// Genera un CSV con headers + una fila de ejemplo real (no comentada; el
// usuario la reemplaza con sus datos). BOM UTF-8 para que Excel abra bien
// los acentos.
const EXAMPLE_ROW = [
  'LV-ABC',       // matricula
  '15/08/2025',   // fecha
  'Juan Pérez',   // piloto
  'ENT',          // finalidad
  '',             // detalle_finalidad
  '10:30',        // salida
  'SAVY',         // desde
  'SAVY',         // hasta
  '11:15',        // llegada
  '0:45',         // tiempo_vuelo
  '3',            // aterrizajes
  '1L',           // aceite
  'Ana García',   // instructor
  '1234.5',       // hobbs_inicio
  '1235.3',       // hobbs_fin
  'Efectivo',     // pago
  'entrenamiento circuito', // observaciones
];

const csvEscape = (v) => {
  const s = String(v == null ? '' : v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export const downloadTemplate = () => {
  const bom = '﻿';
  const csv = bom + [
    TEMPLATE_HEADERS.map(csvEscape).join(','),
    EXAMPLE_ROW.map(csvEscape).join(','),
  ].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla_planillas_condor360.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Detecta si un File es una planilla xlsx/xls/csv (por extensión o MIME).
export const isSpreadsheetFile = (file) => {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  if (/\.(xlsx|xls|csv)$/i.test(name)) return true;
  const t = String(file.type || '').toLowerCase();
  return t === 'text/csv' || t.includes('spreadsheet') || t.includes('excel');
};
